import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { createMessageId } from "../../helpers/onboardingAgentHelpers";
import type { OnboardingAgentResponse } from "../../onboardingAgent.types";
import { onboardingAgentTrackingService } from "../../onboardingAgentTrackingService";
import {
    ONBOARDING_AGENT_DIRECT_LINK_MESSAGE,
    ONBOARDING_AGENT_DIRECT_LINK_WEBSITE_CONTENT_LABEL,
    ONBOARDING_AGENT_DIRECT_LINK_WEBSITE_PERFORMANCE_LABEL,
} from "../../texts";
import {
    buildDirectLinkHref,
    extractSoleWebsiteDomain,
    truncateDirectLinkAddress,
} from "../helpers/directLinkDetection";
import {
    createConversationPrint,
    createDirectLinkAssistantMessage,
    createOnboardingAgentRequest,
    createQuickLinksTrackingPayload,
    isLimitReachedError,
    mapResponseToAssistantMessage,
} from "../helpers/OnboardingAgentPanel.helpers";
import {
    loadOnboardingAgentConversation,
    saveOnboardingAgentConversation,
} from "../helpers/onboardingAgentSessionStorage";
import type {
    ChatMessage,
    CloseReason,
    ReplyErrorState,
    UseOnboardingAgentPanelParams,
    UseOnboardingAgentPanelResult,
} from "../OnboardingAgentPanel.types";

import type { OnboardingAgentMessageVariables } from "./useOnboardingAgentMessageMutation";
import { useOnboardingAgentMessageMutation } from "./useOnboardingAgentMessageMutation";
import { useOnboardingAgentPanelAutoScroll } from "./useOnboardingAgentPanelAutoScroll";

export const useOnboardingAgentPanel = ({
    onClose,
    isPanelOpen,
}: UseOnboardingAgentPanelParams): UseOnboardingAgentPanelResult => {
    const [initialConversation] = useState(loadOnboardingAgentConversation);
    const [messages, setMessages] = useState<ChatMessage[]>(initialConversation.messages);
    const [replyError, setReplyError] = useState<ReplyErrorState | null>(null);
    const [threadId, setThreadId] = useState<string | null>(initialConversation.threadId);
    const [activeRequest, setActiveRequest] = useState<OnboardingAgentMessageVariables | null>(null);
    const [composerFocusTrigger, setComposerFocusTrigger] = useState(0);
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const { translate } = useTranslate();

    const handledResponseRequestIdsRef = useRef(new Set<string>());
    const stoppedRequestIdsRef = useRef(new Set<string>());
    const focusAfterResponseRequestIdRef = useRef<string | null>(null);
    const trackedErrorRequestIdsRef = useRef(new Set<string>());
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollContentRef = useRef<HTMLElement>(null);
    const scrollSpacerRef = useRef<HTMLDivElement>(null);
    const lastUserMessageRef = useRef<HTMLDivElement>(null);
    const queriesCountRef = useRef(0);
    const [initialSessionStartedAt] = useState(() => Date.now());
    const sessionStartedAtRef = useRef(initialSessionStartedAt);
    const hasTrackedSessionEndRef = useRef(false);
    const wasPanelOpenRef = useRef(isPanelOpen);
    const isPanelOpenRef = useRef(isPanelOpen);
    const isFirstOpenScrollRef = useRef(true);
    const ignoreClickOutsideRef = useRef(false);
    const ignoreClickOutsideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sendAgentMessageRef = useRef<((variables: OnboardingAgentMessageVariables) => void) | null>(null);

    const resetSession = useCallback((): void => {
        queriesCountRef.current = 0;
        sessionStartedAtRef.current = Date.now();
        hasTrackedSessionEndRef.current = false;
    }, []);

    const resetRequestTracking = useCallback((): void => {
        handledResponseRequestIdsRef.current.clear();
        stoppedRequestIdsRef.current.clear();
        trackedErrorRequestIdsRef.current.clear();
        focusAfterResponseRequestIdRef.current = null;
    }, []);

    const startRequest = useCallback((request: OnboardingAgentMessageVariables): void => {
        handledResponseRequestIdsRef.current.delete(request.requestId);
        isFirstOpenScrollRef.current = false;
        setActiveRequest(request);
        sendAgentMessageRef.current?.(request);
    }, []);

    const handleMutationSuccess = useCallback(
        (response: OnboardingAgentResponse, variables: OnboardingAgentMessageVariables): void => {
            if (stoppedRequestIdsRef.current.has(variables.requestId)) {
                return;
            }

            if (handledResponseRequestIdsRef.current.has(variables.requestId)) {
                return;
            }

            handledResponseRequestIdsRef.current.add(variables.requestId);
            focusAfterResponseRequestIdRef.current = variables.requestId;

            const responseThreadId = response.threadId || variables.threadId || undefined;

            if (response.threadId) {
                setThreadId(response.threadId);
            }

            if (!variables.isSilent) {
                onboardingAgentTrackingService.trackUserReply({
                    threadId: responseThreadId,
                    userReply: variables.message,
                });
                onboardingAgentTrackingService.trackAgentReply({
                    agentReply: response.responseText,
                    agentQuickLinks: createQuickLinksTrackingPayload(response.suggestedPages),
                    threadId: responseThreadId,
                });
            }

            setReplyError(null);
            const newAssistantMessageId = createMessageId();
            setMessages((previous) => [
                ...previous,
                {
                    id: newAssistantMessageId,
                    role: "assistant",
                    ...mapResponseToAssistantMessage(response),
                    threadId: responseThreadId,
                },
            ]);
            if (isPanelOpenRef.current) {
                setStreamingMessageId(newAssistantMessageId);
            }
        },
        [],
    );

    const handleMutationError = useCallback(
        (error: Error, variables: OnboardingAgentMessageVariables): void => {
            if (stoppedRequestIdsRef.current.has(variables.requestId)) {
                return;
            }

            const failedThreadId = variables.threadId || undefined;

            if (isLimitReachedError(error)) {
                onboardingAgentTrackingService.trackLimitReached({
                    errorMessage: error.message,
                    threadId: failedThreadId,
                });
            }

            if (!trackedErrorRequestIdsRef.current.has(variables.requestId)) {
                trackedErrorRequestIdsRef.current.add(variables.requestId);
                onboardingAgentTrackingService.trackErrorShown({
                    errorMessage: error.message,
                    threadId: failedThreadId,
                });
            }

            setReplyError({
                onRetry: (): void => {
                    setReplyError(null);
                    startRequest({ ...variables, requestId: createMessageId() });
                },
            });
        },
        [startRequest],
    );

    const handleMutationSettled = useCallback((variables: OnboardingAgentMessageVariables): void => {
        stoppedRequestIdsRef.current.delete(variables.requestId);

        if (focusAfterResponseRequestIdRef.current === variables.requestId) {
            focusAfterResponseRequestIdRef.current = null;
            setComposerFocusTrigger((previous) => previous + 1);
        }

        setActiveRequest((current) => (current?.requestId === variables.requestId ? null : current));
    }, []);

    const { send: sendAgentMessage, cancel: cancelAgentMessage } = useOnboardingAgentMessageMutation({
        onSuccess: handleMutationSuccess,
        onError: handleMutationError,
        onSettled: handleMutationSettled,
    });

    sendAgentMessageRef.current = sendAgentMessage;

    useEffect(() => {
        saveOnboardingAgentConversation({ messages, threadId });
    }, [messages, threadId]);

    const handleSend = useCallback(
        (rawText: string): void => {
            const text = rawText.trim();

            if (!text) {
                return;
            }

            if (activeRequest) {
                return;
            }

            queriesCountRef.current += 1;
            setReplyError(null);
            setMessages((previous) => [...previous, { id: createMessageId(), role: "user", text }]);

            const detectedDomain = extractSoleWebsiteDomain(text);

            if (detectedDomain) {
                onboardingAgentTrackingService.trackDirectLinkDetected({
                    threadId: threadId || undefined,
                    detectedDomain,
                });

                const directLinkMessageId = createMessageId();

                setMessages((previous) => [
                    ...previous,
                    {
                        id: directLinkMessageId,
                        role: "assistant",
                        threadId: threadId || undefined,
                        ...createDirectLinkAssistantMessage(detectedDomain, {
                            message: translate(ONBOARDING_AGENT_DIRECT_LINK_MESSAGE, {
                                url: truncateDirectLinkAddress(text),
                                href: buildDirectLinkHref(text),
                            }),
                            websitePerformanceLabel: translate(ONBOARDING_AGENT_DIRECT_LINK_WEBSITE_PERFORMANCE_LABEL),
                            websiteContentLabel: translate(ONBOARDING_AGENT_DIRECT_LINK_WEBSITE_CONTENT_LABEL),
                        }),
                    },
                ]);

                if (isPanelOpenRef.current) {
                    setStreamingMessageId(directLinkMessageId);
                }

                return;
            }

            const nextRequest = createOnboardingAgentRequest(text, threadId);
            startRequest(nextRequest);
        },
        [activeRequest, threadId, startRequest, translate],
    );

    const handleExploreNextLanding = useCallback((): void => {
        // Product decision: do not auto-send a contextual API request after Explore Next in-app navigation.
    }, []);

    const handleStreamComplete = useCallback((messageId: string): void => {
        setStreamingMessageId((current) => (current === messageId ? null : current));
    }, []);

    const handleStop = useCallback((): void => {
        if (activeRequest) {
            stoppedRequestIdsRef.current.add(activeRequest.requestId);
            const activeThreadId = activeRequest.threadId || undefined;
            onboardingAgentTrackingService.trackStopRequest(activeThreadId);
        }

        cancelAgentMessage();
        setActiveRequest(null);
    }, [activeRequest, cancelAgentMessage]);

    const handleNewChat = useCallback((): void => {
        onboardingAgentTrackingService.trackNewChat(threadId || undefined);

        resetRequestTracking();

        if (activeRequest) {
            stoppedRequestIdsRef.current.add(activeRequest.requestId);
            cancelAgentMessage();
            setActiveRequest(null);
        }

        resetSession();

        setMessages([]);
        setThreadId(null);
        setReplyError(null);
        setStreamingMessageId(null);
        setComposerFocusTrigger((previous) => previous + 1);
    }, [activeRequest, cancelAgentMessage, resetRequestTracking, resetSession, threadId]);

    const handleClose = useCallback(
        (reason: CloseReason): void => {
            const trackingThreadId = threadId || undefined;

            onboardingAgentTrackingService.trackChatbotClose(reason, trackingThreadId);

            if (!hasTrackedSessionEndRef.current) {
                hasTrackedSessionEndRef.current = true;
                onboardingAgentTrackingService.trackSessionEnded({
                    queriesCount: queriesCountRef.current,
                    sessionDurationMs: Date.now() - sessionStartedAtRef.current,
                    conversationPrint: createConversationPrint(messages),
                    threadId: trackingThreadId,
                });
            }

            onClose();
        },
        [messages, onClose, threadId],
    );

    const handleCloseButtonClick = useCallback((): void => {
        handleClose("close_button");
    }, [handleClose]);

    const handleClickOutside = useCallback((): void => {
        if (ignoreClickOutsideRef.current) {
            ignoreClickOutsideRef.current = false;
            return;
        }

        handleClose("click_outside");
    }, [handleClose]);

    useEffect(() => {
        isPanelOpenRef.current = isPanelOpen;
        if (!isPanelOpen) {
            wasPanelOpenRef.current = false;
            ignoreClickOutsideRef.current = false;
            if (ignoreClickOutsideTimeoutRef.current) {
                clearTimeout(ignoreClickOutsideTimeoutRef.current);
                ignoreClickOutsideTimeoutRef.current = null;
            }
            setStreamingMessageId(null);
            isFirstOpenScrollRef.current = true;
            return;
        }

        if (!wasPanelOpenRef.current) {
            ignoreClickOutsideRef.current = true;
            ignoreClickOutsideTimeoutRef.current = setTimeout(() => {
                ignoreClickOutsideRef.current = false;
                ignoreClickOutsideTimeoutRef.current = null;
            }, 0);
            resetSession();
        }
        wasPanelOpenRef.current = true;
    }, [isPanelOpen, resetSession]);

    useEffect(
        () => () => {
            if (ignoreClickOutsideTimeoutRef.current) {
                clearTimeout(ignoreClickOutsideTimeoutRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!isPanelOpen) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                handleClose("escape");
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleClose, isPanelOpen]);

    const userMessagesLength = messages.reduce((count, message) => count + (message.role === "user" ? 1 : 0), 0);

    useOnboardingAgentPanelAutoScroll({
        isPanelOpen,
        userMessagesLength,
        replyError,
        scrollContainerRef,
        scrollContentRef,
        scrollSpacerRef,
        lastUserMessageRef,
        isFirstOpenScrollRef,
    });

    return {
        messages,
        replyError,
        activeRequest,
        composerFocusTrigger,
        streamingMessageId,
        scrollContainerRef,
        scrollContentRef,
        scrollSpacerRef,
        lastUserMessageRef,
        threadId,
        handleCloseButtonClick,
        handleClickOutside,
        handleSend,
        handleExploreNextLanding,
        handleStop,
        handleNewChat,
        handleStreamComplete,
    };
};
